import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock3, Loader2, RefreshCw, Send, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import {
  alignMinutesToSlotInterval,
  defaultDurationMinutes,
  normalizeSlotIntervalMinutes,
} from "@/lib/calendarGranularity";
import { buildTimeChipSlots } from "./TimeChipGrid";
import TimeSelect from "./TimeSelect";

const confirmRequiredMessage = "Pick doctor, chair, date, time, procedure, and duration.";

export default function BookingRequestsPanel({ branchId = "", doctors = [], chairs = [], settings = null, readOnly = false, onChanged }) {
  const [rows, setRows] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [declineDrafts, setDeclineDrafts] = useState({});

  const load = useCallback(async () => {
    if (!branchId) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const [{ data, error }, proceduresRes] = await Promise.all([
      supabase.rpc("list_patient_booking_requests", { p_status: "open", p_branch_id: branchId }),
      supabase.rpc("list_clinic_procedures"),
    ]);
    setLoading(false);
    if (error) {
      toast.error(error.message || "Could not load booking requests.");
      return;
    }
    if (!proceduresRes.error) setProcedures(proceduresRes.data || []);
    const next = data || [];
    setRows(next);
    setDrafts((current) => seedDrafts(current, next, chairs, doctors, settings));
  }, [branchId, chairs, doctors, settings]);

  useEffect(() => { load(); }, [load]);

  const count = rows.length;

  function patchDraft(rowId, patch) {
    setDrafts((current) => ({ ...current, [rowId]: { ...(current[rowId] || {}), ...patch } }));
  }

  async function markContacted(row) {
    setBusyKey(`${row.id}:contacted`);
    const { error } = await supabase.rpc("set_patient_booking_request_contacted", { p_request_id: row.id });
    setBusyKey(null);
    if (error) {
      toast.error(error.message || "Could not mark contacted.");
      return;
    }
    toast.success("Request marked contacted.");
    await load();
    onChanged?.();
  }

  async function decline(row) {
    const reason = (declineDrafts[row.id] || "").trim();
    if (!reason) {
      toast.error("Decline reason is required.");
      return;
    }
    setBusyKey(`${row.id}:decline`);
    const { error } = await supabase.rpc("decline_patient_booking_request", {
      p_request_id: row.id,
      p_reason: reason,
    });
    setBusyKey(null);
    if (error) {
      toast.error(error.message || "Could not decline request.");
      return;
    }
    toast.success("Request declined.");
    await load();
    onChanged?.();
  }

  async function mergePossibleMatch(row) {
    if (!row.possible_match_patient_id) return;
    setBusyKey(`${row.id}:merge`);
    const { error } = await supabase.rpc("merge_self_registered_patient", {
      p_source_patient_id: row.patient_id,
      p_surviving_patient_id: row.possible_match_patient_id,
      p_reason: "possible match reviewed from booking request",
    });
    setBusyKey(null);
    if (error) {
      toast.error(error.message || "Could not merge records.");
      return;
    }
    toast.success("Patient records merged.");
    await load();
    onChanged?.();
  }

  async function confirm(row) {
    const draft = drafts[row.id] || {};
    if (!draft.doctor_id || !draft.chair_id || !draft.date || !draft.time || !draft.duration_minutes) {
      toast.error(confirmRequiredMessage);
      return;
    }
    if (!draft.procedure_id) {
      toast.error(confirmRequiredMessage);
      return;
    }
    const scheduledStart = new Date(`${draft.date}T${draft.time}:00`).toISOString();
    const notes = buildConfirmationNotes(row);
    setBusyKey(`${row.id}:confirm`);
    const { error } = await supabase.rpc("confirm_patient_booking_request", {
      p_request_id: row.id,
      p_input: {
        doctor_id: draft.doctor_id,
        chair_id: draft.chair_id,
        scheduled_start: scheduledStart,
        procedure_id: draft.procedure_id,
        duration_minutes: Number(draft.duration_minutes),
        notes,
      },
    });
    setBusyKey(null);
    if (error) {
      toast.error(error.message || "Could not confirm request.");
      return;
    }
    toast.success("Appointment booked.");
    await load();
    onChanged?.();
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="booking-requests-panel">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-3 py-3">
        <div>
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-teal-600" />
            <h2 className="text-sm font-bold text-slate-950">Incoming requests</h2>
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">{count}</span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">Public booking requests waiting for staff confirmation.</p>
        </div>
        <button type="button" onClick={load} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Refresh booking requests">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>

      <div className="space-y-2 p-3">
        {loading ? (
          <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-4 text-xs text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading booking requests...
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-4 text-sm text-slate-500">
            No incoming requests.
          </div>
        ) : rows.map((row) => (
          <RequestCard
            key={row.id}
            row={row}
            branchId={branchId}
            doctors={doctors}
            chairs={chairs}
            procedures={procedures}
            draft={drafts[row.id] || {}}
            settings={settings}
            declineReason={declineDrafts[row.id] || ""}
            busyKey={busyKey}
            readOnly={readOnly}
            onDraftChange={(patch) => patchDraft(row.id, patch)}
            onDeclineReasonChange={(value) => setDeclineDrafts((current) => ({ ...current, [row.id]: value }))}
            onContacted={() => markContacted(row)}
            onDecline={() => decline(row)}
            onMerge={() => mergePossibleMatch(row)}
            onConfirm={() => confirm(row)}
          />
        ))}
      </div>
    </section>
  );
}

const fieldClass = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100";

function RequestCard({
  row,
  branchId,
  doctors,
  chairs,
  procedures,
  draft,
  settings,
  declineReason,
  busyKey,
  readOnly,
  onDraftChange,
  onDeclineReasonChange,
  onContacted,
  onDecline,
  onMerge,
  onConfirm,
}) {
  const [dayAppointments, setDayAppointments] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const slotIntervalMinutes = normalizeSlotIntervalMinutes(settings?.slot_interval_minutes);
  const fallbackDurationMin = defaultDurationMinutes(settings, slotIntervalMinutes);

  const filteredChairs = useMemo(() => {
    if (!row.branch_id) return chairs;
    return chairs.filter((chair) => !chair.branch_id || chair.branch_id === row.branch_id);
  }, [chairs, row.branch_id]);
  const activeProcedures = useMemo(() => {
    return [...(procedures || [])]
      .filter((procedure) => !procedure.archived && !procedure.ortho_visit_type)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [procedures]);
  const busy = busyKey?.startsWith(`${row.id}:`);
  const missingConfirmFields = !draft.doctor_id || !draft.chair_id || !draft.date || !draft.time || !draft.duration_minutes || !draft.procedure_id;
  const timeSlots = useMemo(() => {
    return buildStaffTimeSlots({
      date: draft.date,
      duration_minutes: draft.duration_minutes || fallbackDurationMin,
      slotIntervalMinutes,
      chair_id: draft.chair_id,
      doctor_id: draft.doctor_id,
      appointments: dayAppointments,
      chairs,
    });
  }, [draft.date, draft.duration_minutes, draft.chair_id, draft.doctor_id, fallbackDurationMin, slotIntervalMinutes, dayAppointments, chairs]);

  useEffect(() => {
    if (!draft.date) {
      setDayAppointments([]);
      return undefined;
    }
    let cancelled = false;
    const range = localDateRange(draft.date);
    setLoadingTimes(true);
    supabase.rpc("list_appointments", {
      p_from: range.from.toISOString(),
      p_to: range.to.toISOString(),
      p_doctor_id: null,
      p_chair_id: null,
      p_branch_id: row.branch_id || branchId || null,
    }).then(({ data, error }) => {
      if (cancelled) return;
      setLoadingTimes(false);
      setDayAppointments(error ? null : (data || []));
    });
    return () => { cancelled = true; };
  }, [branchId, draft.date, row.branch_id]);

  useEffect(() => {
    if (!draft.time) return;
    if (!timeSlots.some((slot) => slot === draft.time)) {
      onDraftChange({ time: "" });
    }
  // onDraftChange is an inline parent callback; the timeSlots check is the stable trigger.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.time, timeSlots.join("|")]);

  function handleProcedureChange(procedureId) {
    const procedure = activeProcedures.find((row) => row.id === procedureId);
    onDraftChange({
      procedure_id: procedureId,
      time: "",
      ...(procedure?.suggested_duration_minutes
        ? { duration_minutes: alignMinutesToSlotInterval(procedure.suggested_duration_minutes, slotIntervalMinutes) }
        : {}),
    });
  }

  return (
    <article className="rounded-lg border border-slate-200 p-3" data-testid={`booking-request-${row.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate text-sm font-bold text-slate-950">{row.patient_name || "Unnamed patient"}</h3>
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">{row.status}</span>
            {row.possible_match_status === "possible_match_review" && row.possible_match_patient_id ? (
              <span data-testid={`booking-request-possible-match-${row.id}`} className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-900">
                <AlertTriangle className="h-3 w-3" />
                possible match - review
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-slate-500">{row.patient_phone || "No phone"}{row.patient_email ? ` · ${row.patient_email}` : ""}</p>
        </div>
        <p className="text-right text-[11px] text-slate-400">{formatCreated(row.created_at)}</p>
      </div>

      <div className="mt-3 grid gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-700 sm:grid-cols-2">
        <Info label="Preferred" value={row.preferred_anytime ? "Any available time" : `${formatDate(row.preferred_date)} ${formatTime(row.preferred_time)}`} />
        <Info label="Branch" value={row.branch_name || "No preference"} />
        <Info label="Reason" value={row.reason || "Not specified"} />
        <Info label="Note" value={row.note || "None"} />
      </div>

      {row.possible_match_status === "possible_match_review" && row.possible_match_patient_id ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
          <span className="font-semibold">Possible match:</span>
          <span>{row.possible_match_name || "Existing patient"}{row.possible_match_phone ? ` · ${row.possible_match_phone}` : ""}</span>
          <button
            type="button"
            onClick={onMerge}
            disabled={readOnly || busy}
            data-testid={`booking-request-merge-${row.id}`}
            className="ml-auto rounded bg-amber-600 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50"
          >
            Merge
          </button>
        </div>
      ) : null}

      <div className="mt-3 grid gap-2 sm:grid-cols-6">
        <Field label="Doctor">
          <select value={draft.doctor_id || ""} onChange={(event) => onDraftChange({ doctor_id: event.target.value, time: "" })} data-testid={`booking-request-doctor-${row.id}`} className={fieldClass}>
            <option value="">Pick doctor</option>
            {doctors.map((doctor) => <option key={doctor.id} value={doctor.id}>{doctor.display_name || doctor.full_name || "Doctor"}</option>)}
          </select>
        </Field>
        <Field label="Chair">
          <select value={draft.chair_id || ""} onChange={(event) => onDraftChange({ chair_id: event.target.value, time: "" })} data-testid={`booking-request-chair-${row.id}`} className={fieldClass}>
            <option value="">Pick chair</option>
            {filteredChairs.map((chair) => <option key={chair.id} value={chair.id}>{chair.name}</option>)}
          </select>
        </Field>
        <Field label="Procedure">
          <select value={draft.procedure_id || ""} onChange={(event) => handleProcedureChange(event.target.value)} data-testid={`booking-request-procedure-${row.id}`} className={fieldClass}>
            <option value="">Pick procedure</option>
            {activeProcedures.map((procedure) => (
              <option key={procedure.id} value={procedure.id}>
                {procedure.name || "Procedure"}{procedure.suggested_duration_minutes ? ` (${procedure.suggested_duration_minutes} min)` : ""}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date">
          <input type="date" value={draft.date || ""} onChange={(event) => onDraftChange({ date: event.target.value, time: "" })} data-testid={`booking-request-date-${row.id}`} className={fieldClass} />
        </Field>
        <Field label="Minutes">
          <input type="number" min={slotIntervalMinutes} step={slotIntervalMinutes} value={draft.duration_minutes || fallbackDurationMin} onChange={(event) => onDraftChange({ duration_minutes: event.target.value, time: "" })} data-testid={`booking-request-duration-${row.id}`} className={fieldClass} />
        </Field>
        <Field label="Time">
          <div className="relative">
            <TimeSelect
              value={draft.time || ""}
              onChange={(time) => onDraftChange({ time })}
              slots={timeSlots}
              slotIntervalMinutes={slotIntervalMinutes}
              testId={`booking-request-time-${row.id}`}
              className={fieldClass}
              placeholder="Pick time"
            />
            {loadingTimes ? (
              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              </span>
            ) : null}
          </div>
        </Field>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={onConfirm} disabled={readOnly || busy || missingConfirmFields} title={missingConfirmFields ? confirmRequiredMessage : undefined} data-testid={`booking-request-confirm-${row.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-xs font-bold text-white hover:bg-teal-700 disabled:opacity-50">
          {busyKey === `${row.id}:confirm` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          Confirm
        </button>
        <button type="button" onClick={onContacted} disabled={readOnly || busy || row.status === "contacted"} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 disabled:opacity-50">
          <Clock3 className="h-3.5 w-3.5" />
          Contacted
        </button>
        <input
          value={declineReason}
          onChange={(event) => onDeclineReasonChange(event.target.value)}
          placeholder="Decline reason"
          className="min-w-[180px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-xs"
        />
        <button type="button" onClick={onDecline} disabled={readOnly || busy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-50">
          <X className="h-3.5 w-3.5" />
          Decline
        </button>
      </div>
    </article>
  );
}

function seedDrafts(current, rows, chairs, doctors, settings) {
  const slotIntervalMinutes = normalizeSlotIntervalMinutes(settings?.slot_interval_minutes);
  const fallbackDurationMin = defaultDurationMinutes(settings, slotIntervalMinutes);
  const next = { ...current };
  rows.forEach((row) => {
    if (next[row.id]) return;
    const branchChair = row.branch_id ? chairs.find((chair) => chair.branch_id === row.branch_id) : chairs[0];
    next[row.id] = {
      doctor_id: doctors[0]?.id || "",
      chair_id: branchChair?.id || chairs[0]?.id || "",
      date: row.preferred_date || "",
      time: row.preferred_time ? String(row.preferred_time).slice(0, 5) : "",
      duration_minutes: fallbackDurationMin,
      procedure_id: "",
    };
  });
  return next;
}

function buildConfirmationNotes(row) {
  const parts = [];
  if (row.reason) parts.push(`Patient requested: ${row.reason}`);
  if (row.note) parts.push(`Patient note: ${row.note}`);
  return parts.join("\n") || null;
}

function buildStaffTimeSlots({
  date,
  duration_minutes,
  slotIntervalMinutes,
  chair_id,
  doctor_id,
  appointments = [],
  chairs = [],
}) {
  if (appointments === null) return [];
  const interval = normalizeSlotIntervalMinutes(slotIntervalMinutes);
  if (!date) return buildTimeChipSlots({ slotIntervalMinutes: interval });
  const duration = Number(duration_minutes) || interval;
  return buildTimeChipSlots({ slotIntervalMinutes: interval }).filter((time) => {
    return !slotConflicts({
      date,
      time,
      duration,
      chair_id,
      doctor_id,
      appointments,
      chairs,
    });
  });
}

function slotConflicts({
  date,
  time,
  duration,
  chair_id,
  doctor_id,
  appointments,
  chairs,
}) {
  if (!chair_id && !doctor_id) return false;
  const slotStart = new Date(`${date}T${time}:00`).getTime();
  const slotEnd = slotStart + duration * 60_000;
  if (Number.isNaN(slotStart) || Number.isNaN(slotEnd)) return false;

  const chairBranch = branchForChair(chairs, chair_id);
  return appointments.some((appointment) => {
    if (!appointmentBlocksSlot(appointment.status)) return false;
    const apptStart = new Date(appointment.scheduled_start).getTime();
    const apptEnd = new Date(appointment.scheduled_end).getTime();
    if (Number.isNaN(apptStart) || Number.isNaN(apptEnd)) return false;
    if (apptStart >= slotEnd || apptEnd <= slotStart) return false;
    if (chair_id && appointment.chair_id === chair_id) return true;
    if (!doctor_id || appointment.doctor_id !== doctor_id || !chairBranch) return false;
    return branchForChair(chairs, appointment.chair_id) === chairBranch;
  });
}

function appointmentBlocksSlot(status) {
  return ["Scheduled", "Confirmed", "Checked In", "Pencil"].includes(String(status || ""));
}

function branchForChair(chairs, chairId) {
  return chairs.find((chair) => chair.id === chairId)?.branch_id || "";
}

function localDateRange(date) {
  const from = new Date(`${date}T00:00:00`);
  const to = new Date(from);
  to.setDate(to.getDate() + 1);
  return { from, to };
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 font-medium text-slate-700">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function formatDate(value) {
  if (!value) return "Any date";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return value;
  }
}

function formatTime(value) {
  if (!value) return "";
  const [hourRaw, minuteRaw] = String(value).split(":");
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw || 0);
  if (Number.isNaN(hour)) return value;
  return new Date(2026, 0, 1, hour, minute).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatCreated(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return value;
  }
}
